import { Button, Fieldset, Group, Select, SelectProps, TextInput } from '@mantine/core';
import { FormEvent, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FormAttachmentViewer } from 'src/Components/Forms/FormAttachmentViewer';
import {
  getFieldTemplate,
  getValidSubtypesForCategory,
  subtypeFieldTemplates,
  subtypeIcons,
  typeIcons,
} from 'src/Components/Forms/PracticalInformation/PracticalInformationForm/PracticalInformationCategoryMapping.helper';
import { RenderField } from 'src/Components/Forms/PracticalInformation/PracticalInformationForm/RenderField';
import { UploadFileButton } from 'src/Components/Inputs/UploadFileButton';
import { IconSelect } from 'src/Components/Shared/IconSelect';
import ICONS from 'src/Components/Shared/IconSelect/icons';
import { defaultRecurrenceValue } from 'src/Hooks/Forms/UseCreateEventForm/useCreateEventForm';
import { useFetchStorageItemInformation } from 'src/Hooks/Mutations/useFetchStorageItemInformation';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import { UploadableFile, useFileUploadManager } from 'src/Hooks/Shared/useFileUploadManager';
import {
  CreatePracticalInformationDtoSubtypeEnum,
  CreatePracticalInformationDtoTypeEnum,
  CreatePracticalInformationFieldDtoFieldTypeEnum,
} from 'src/Services/API/generated';

import styles from './PracticalInformationForm.module.scss';
import { Props } from './PracticalInformationForm.types';

const ICON_RENDER_SIZE = 18;

const renderIconForKey = (iconKey: string | undefined) => {
  if (!iconKey) return null;
  const IconComponent = ICONS[iconKey as keyof typeof ICONS];
  return IconComponent ? <IconComponent size={ICON_RENDER_SIZE} /> : null;
};

const buildIconOptionRenderer =
  (resolveIcon: (value: string) => string | undefined): SelectProps['renderOption'] =>
  ({ option }) => (
    <Group gap="xs" wrap="nowrap">
      {renderIconForKey(resolveIcon(option.value))}
      <span>{option.label}</span>
    </Group>
  );

const renderTypeOption = buildIconOptionRenderer(
  value => typeIcons[value as CreatePracticalInformationDtoTypeEnum]
);

const renderSubtypeOption = buildIconOptionRenderer(
  value => subtypeIcons[value as CreatePracticalInformationDtoSubtypeEnum]
);

const PracticalInformationForm = ({ form, onSubmit, submitButtonLabel, onCancel }: Props) => {
  const { t } = useTranslation();

  const { data: asset } = useFetchAssetDetails();

  const handleUploadReady = useCallback(
    (readyFile: UploadableFile) => {
      if (!readyFile.fileData) {
        console.error('readyFile.fileData should not be undefined.');
        return;
      }

      form.setFieldValue('attachments', (current: typeof form.values.attachments) => [
        ...current,
        {
          fileId: readyFile.fileData!.id,
          type: readyFile.fileData!.type,
          name: readyFile.fileData!.name,
          previewUrl: readyFile.fileData!.urls?.previewThumbnailUrl,
        },
      ]);
    },
    [form]
  );

  const {
    uploads,
    startUpload,
    removeUpload,
    isLoading: isUploading,
  } = useFileUploadManager({
    onUploadReady: handleUploadReady,
  });

  const { mutateAsync: fetchFileInfo } = useFetchStorageItemInformation();

  const handleExistingFilesSelect = async (fileIds: string[]) => {
    const newAttachments = await Promise.all(
      fileIds.map(async fileId => {
        const fileInfo = await fetchFileInfo(fileId);
        return {
          fileId: fileInfo.id,
          type: fileInfo.type,
          name: fileInfo.name,
          previewUrl: fileInfo.urls?.previewThumbnailUrl,
        };
      })
    );
    form.setFieldValue('attachments', (current: typeof form.values.attachments) => [
      ...current,
      ...newAttachments,
    ]);
  };

  const createPracticalInformation = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      if (!asset) {
        event.preventDefault();
        return;
      }

      return onSubmit(event);
    },
    [asset, onSubmit]
  );

  const handleSubtypeChange = useCallback(
    (value?: string | null) => {
      // First update the subtype field
      form.getInputProps('subtype').onChange(value);

      // If the value is a valid subtype, update the fields based on the template
      if (value && value in subtypeFieldTemplates) {
        const subtype = value as CreatePracticalInformationDtoSubtypeEnum;

        // Update the icon
        form.setFieldValue('icon', subtypeIcons[subtype]);

        // Get the field templates for this subtype
        const templates = subtypeFieldTemplates[subtype] ?? [
          { fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text },
        ];

        // Generate fields based on the template
        const fields = templates.map(template => ({
          fieldType: template.fieldType,
          value:
            form.values.fields.find(field => field.key === template.key)?.value ??
            (template.fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Schedule
              ? defaultRecurrenceValue
              : undefined),
          key: template.key,
          required: false,
        }));

        // Update the form fields
        form.setFieldValue('fields', fields);
      }
    },
    [form]
  );

  const handleTypeChange = useCallback(
    (value: CreatePracticalInformationDtoTypeEnum | null) => {
      if (value) {
        // Get valid subtypes for the selected category
        const subtypes = getValidSubtypesForCategory(value);
        // Set the first subtype as default if available
        const defaultSubtype = subtypes?.[0];
        form.setFieldValue('subtype', defaultSubtype);

        if (value === CreatePracticalInformationDtoTypeEnum.Custom) {
          // For custom type, set a simple text field
          form.setFieldValue('fields', [
            {
              fieldType: CreatePracticalInformationFieldDtoFieldTypeEnum.Text,
              value: '',
              key: 'custom',
            },
          ]);
          return;
        }
      }

      form.setFieldValue('fields', []);
    },
    [form, handleSubtypeChange]
  );

  const validSubtypes = useMemo(
    () => getValidSubtypesForCategory(form.values.type),
    [form.values.type]
  );

  useEffect(() => {
    // Skip the destructive reset when a subtype was provided via initialValues
    // (preselection from suggestions): the subtype useEffect below will hydrate fields.
    if (!form.values.subtype) {
      handleTypeChange(form.values.type);
    }
  }, []);

  useEffect(() => {
    if (form.values.subtype) {
      handleSubtypeChange(form.values.subtype);
    }
  }, [form.values.subtype]);

  return (
    <form onSubmit={createPracticalInformation} className={styles.container}>
      <div className={styles.topLine}>
        <Select
          label={t('practicalInformation.typeLabel', "Type d'information pratique")}
          data={Object.values(CreatePracticalInformationDtoTypeEnum).map(type => ({
            value: type,
            label: t(`practicalInformation.types.${type}`),
          }))}
          allowDeselect={false}
          renderOption={renderTypeOption}
          leftSection={renderIconForKey(typeIcons[form.values.type])}
          {...form.getInputProps('type')}
          onChange={value => {
            handleTypeChange(value as CreatePracticalInformationDtoTypeEnum);
            form.getInputProps('type').onChange(value);
          }}
        />
        <IconSelect {...form.getInputProps('icon')} />
      </div>
      {validSubtypes ? (
        <Select
          data={validSubtypes.map(subtype => ({
            value: subtype,
            label: t(`practicalInformation.subtypes.${subtype}`),
          }))}
          allowDeselect={false}
          renderOption={renderSubtypeOption}
          leftSection={renderIconForKey(
            form.values.subtype ? subtypeIcons[form.values.subtype] : undefined
          )}
          {...form.getInputProps('subtype')}
          onChange={handleSubtypeChange}
        />
      ) : (
        <TextInput
          {...form.getInputProps('title')}
          placeholder={t(
            'practicalInformation.titlePlaceholder',
            'Titre de l\'information pratique (exemple : “Tri sélectif - Bac jaune")'
          )}
          label={t('practicalInformation.titleLabel', 'Titre')}
        />
      )}

      {form.values.fields.map((field, index) => {
        const template = getFieldTemplate(field.key);
        return template ? (
          <RenderField
            key={['renderField', index, field.key].join('-')}
            field={template}
            index={index}
            form={form}
            t={t}
          />
        ) : null;
      })}

      {(() => {
        const dateOrScheduleFields = form.values.fields.filter(
          field =>
            field.fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Date ||
            field.fieldType === CreatePracticalInformationFieldDtoFieldTypeEnum.Schedule
        );

        if (dateOrScheduleFields.length === 0) return null;

        const reminderDurationOptions = [
          { value: '0', label: t('practicalInformation.reminder.atTime', 'À la date') },
          { value: '3600', label: t('practicalInformation.reminder.1hBefore', '1 heure avant') },
          { value: '86400', label: t('practicalInformation.reminder.1dBefore', '1 jour avant') },
          { value: '259200', label: t('practicalInformation.reminder.3dBefore', '3 jours avant') },
          {
            value: '604800',
            label: t('practicalInformation.reminder.1wBefore', '1 semaine avant'),
          },
          {
            value: '2592000',
            label: t('practicalInformation.reminder.30dBefore', '30 jours avant'),
          },
        ];

        return (
          <Fieldset
            legend={t('practicalInformation.reminder.title', 'Rappel')}
            className={styles.reminderBlock}
          >
            <Select
              label={t('practicalInformation.reminder.fieldLabel', 'Sur quelle date ?')}
              data={dateOrScheduleFields.map(field => ({
                value: field.key,
                label: t(`practicalInformation.placeholders.${field.key}`, field.key),
              }))}
              clearable
              placeholder={t('practicalInformation.reminder.noReminder', 'Pas de rappel')}
              className={styles.reminderFieldSelect}
              {...form.getInputProps('reminderFieldKey')}
              onChange={value => {
                form.setFieldValue('reminderFieldKey', value ?? undefined);
                if (!value) {
                  form.setFieldValue('reminderDurationSeconds', undefined);
                }
              }}
            />
            {form.values.reminderFieldKey && (
              <Select
                label={t('practicalInformation.reminder.whenLabel', 'Quand alerter ?')}
                data={reminderDurationOptions}
                allowDeselect={false}
                value={form.values.reminderDurationSeconds?.toString() ?? '0'}
                onChange={value =>
                  form.setFieldValue(
                    'reminderDurationSeconds',
                    value ? parseInt(value, 10) : undefined
                  )
                }
                mt="xs"
              />
            )}
          </Fieldset>
        );
      })()}

      <div className={styles.uploadButtonContainer}>
        <UploadFileButton
          onFilesSelected={files => startUpload(files)}
          onExistingFilesSelect={handleExistingFilesSelect}
          loading={isUploading}
          modalTitle={t(
            'practicalInformation.attachments.modalTitle',
            "Ajouter des pièces jointes à l'info pratique"
          )}
        />
      </div>
      <FormAttachmentViewer
        attachments={form.values.attachments}
        uploadingAttachments={uploads}
        onDeleteAttachment={attachment =>
          form.setFieldValue(
            'attachments',
            form.values.attachments.filter(a => a.fileId !== attachment.fileId)
          )
        }
        onDeleteUploadingAttachment={localId => removeUpload(localId)}
      />

      <div className={styles.border} />
      <div className={styles.buttonsContainer}>
        <Button onClick={onCancel} variant="outline" className="btn-medium">
          {t('practicalInformation.cancel', 'Annuler')}
        </Button>
        <Button type="submit" variant="primary">
          {submitButtonLabel}
        </Button>
      </div>
    </form>
  );
};

export default PracticalInformationForm;
